import React, { useState } from 'react'
import {
  Grid,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
} from '@material-ui/core'
import styled from 'styled-components'
import { CompanySnapshotItem, CompanySnapshotStatus } from '../../../../types/migration'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import ErrorIcon from '@material-ui/icons/Error'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import AccessTimeIcon from '@material-ui/icons/AccessTime'

const GridRow = styled(Grid)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  minHeight: 70,
  width: '100%',
  '& .MuiGrid-item': {
    display: 'flex',
    alignItems: 'center',
  },
})

const HeaderText = styled(Typography)({
  fontWeight: 600,
  fontSize: 14,
  color: '#666',
})

const StatusText = styled(Typography)<{ status?: CompanySnapshotStatus }>`
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  color: ${(props) => {
    switch (props.status) {
      case CompanySnapshotStatus.COMPLETED:
        return '#4CAF50'
      case CompanySnapshotStatus.ERROR:
        return '#f44336'
      case CompanySnapshotStatus.IN_PROGRESS:
        return '#ff9800'
      case CompanySnapshotStatus.EXPIRED:
        return '#757575'
      default:
        return '#666'
    }
  }};

  &:hover {
    text-decoration: underline;
    opacity: 0.8;
  }
`

const StyledGenerateButton = styled(Button)<{ isGenerating: boolean }>`
  min-width: 100px;
  border-width: 0px;
  text-transform: none !important;

  ${(props) =>
    props.isGenerating
      ? `
    color: #e65100;
  `
      : `
    color: #2e7d32;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
    }
    
    &:disabled {
      color: #9e9e9e;
      box-shadow: none;
      transform: none;
      
      &:hover {
        transform: none;
        box-shadow: none;
      }
    }
  `}
`

const StyledDownloadButton = styled(IconButton)<{ canDownload: boolean }>`
  transition: all 0.3s ease;
  border-width: 0px;

  ${(props) =>
    props.canDownload
      ? `
    color: #1976d2;
    &:hover {
      color: #0d47a1;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(33, 150, 243, 0.4);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(33, 150, 243, 0.5);
    }
  `
      : `
    color: #9e9e9e;
    box-shadow: none;
    
    &:hover {
      background-color: #f5f5f5;
      transform: none;
    }
    
    &:disabled {
      color: #bdbdbd;
    }
  `}
`

interface CompanySnapshotGroupProps {
  isLoading?: boolean
  isCompanySelected?: boolean
  hasAvailableShops?: boolean
  latestSnapshot?: CompanySnapshotItem
  onGenerateSnapshot?: () => void
  onDownloadSnapshot?: (snapshot: CompanySnapshotItem) => void
  isGenerating?: boolean
}

// Helper function to check if there's a valid snapshot available for download
export const hasValidSnapshot = (snapshot?: CompanySnapshotItem): boolean => {
  if (!snapshot) return false

  const oneHourAgo = Date.now() - 60 * 60 * 1000
  const isRecent = snapshot.requestDate > oneHourAgo

  return snapshot.status === CompanySnapshotStatus.COMPLETED && isRecent
}

const SnapshotHeaderRow: React.FC = () => {
  return (
    <GridRow container spacing={16} style={{ padding: '8px 0' }}>
      <Grid item xs={6} sm={4}>
        <HeaderText>Products</HeaderText>
      </Grid>
      <Grid item xs={12} sm={4}>
        <HeaderText>Snapshot Status</HeaderText>
      </Grid>
      <Grid item xs={6} sm={4}>
        <HeaderText>Download</HeaderText>
      </Grid>
    </GridRow>
  )
}

const getSnapshotStatus = (snapshot?: CompanySnapshotItem): CompanySnapshotStatus => {
  if (!snapshot) return CompanySnapshotStatus.ERROR

  const oneHourAgo = Date.now() - 60 * 60 * 1000
  const isRecent = snapshot.requestDate > oneHourAgo

  return snapshot.status === CompanySnapshotStatus.COMPLETED
    ? isRecent
      ? CompanySnapshotStatus.COMPLETED
      : CompanySnapshotStatus.EXPIRED
    : snapshot.status
}

const getStatusIcon = (status: CompanySnapshotStatus) => {
  switch (status) {
    case CompanySnapshotStatus.COMPLETED:
      return <CheckCircleIcon style={{ color: '#4CAF50', fontSize: 20 }} />
    case CompanySnapshotStatus.ERROR:
      return <ErrorIcon style={{ color: '#f44336', fontSize: 20 }} />
    case CompanySnapshotStatus.IN_PROGRESS:
      return <CircularProgress size={20} style={{ color: '#ff9800' }} />
    case CompanySnapshotStatus.EXPIRED:
      return <AccessTimeIcon style={{ color: '#757575', fontSize: 20 }} />
    default:
      return null
  }
}

const SnapshotInfoModal: React.FC<{
  open: boolean
  onClose: () => void
  snapshot?: CompanySnapshotItem
}> = ({ open, onClose, snapshot }) => {
  if (!snapshot) return null

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp)
    return {
      fullDate: date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      }),
      relativeTime: getRelativeTime(timestamp),
    }
  }

  const getRelativeTime = (timestamp: number) => {
    const now = Date.now()
    const diffMs = now - timestamp
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMinutes < 1) return 'Just now'
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
  }

  const { fullDate, relativeTime } = formatDate(snapshot.requestDate)
  const status = getSnapshotStatus(snapshot)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Grid container alignItems="center" spacing={8}>
          <Grid item>{getStatusIcon(status)}</Grid>
          <Grid item>
            <Typography variant="title">Snapshot Information</Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={16}>
          <Grid item>
            <Typography variant="subheading" color="textSecondary" gutterBottom>
              Status
            </Typography>
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              {status}
            </Typography>
          </Grid>

          <Grid item>
            <Typography variant="subheading" color="textSecondary" gutterBottom>
              Request Date
            </Typography>
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              {relativeTime}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {fullDate}
            </Typography>
          </Grid>

          {snapshot.asset?.name && (
            <Grid item>
              <Typography variant="subheading" color="textSecondary" gutterBottom>
                File Name
              </Typography>
              <Typography
                variant="body2"
                style={{
                  fontFamily: 'monospace',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: 4,
                }}
              >
                {snapshot.asset.name}
              </Typography>
            </Grid>
          )}

          {snapshot.asset?.key && (
            <Grid item>
              <Typography variant="subheading" color="textSecondary" gutterBottom>
                Asset Key
              </Typography>
              <Typography
                variant="body2"
                style={{
                  fontFamily: 'monospace',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: 4,
                }}
              >
                {snapshot.asset.key}
              </Typography>
            </Grid>
          )}

          <Grid item>
            <Typography variant="subheading" color="textSecondary" gutterBottom>
              Snapshot ID
            </Typography>
            <Typography
              variant="body2"
              style={{
                fontFamily: 'monospace',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: 4,
              }}
            >
              {snapshot.id}
            </Typography>
          </Grid>

          {status === CompanySnapshotStatus.EXPIRED && (
            <Grid item>
              <Chip
                label="⚠️ This snapshot has expired. Generate a new one to continue."
                color="secondary"
                style={{
                  color: '#e65100',
                  fontWeight: 500,
                }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const SnapshotRow: React.FC<{
  snapshot?: CompanySnapshotItem
  onGenerateSnapshot?: () => void
  onDownloadSnapshot?: (snapshot: CompanySnapshotItem) => void
  onStatusClick?: () => void
  isGenerating?: boolean
  disabled?: boolean
}> = ({
  snapshot,
  onGenerateSnapshot,
  onDownloadSnapshot,
  onStatusClick,
  isGenerating = false,
  disabled = false,
}) => {
  const status = getSnapshotStatus(snapshot)
  const canDownload = status === CompanySnapshotStatus.COMPLETED && snapshot
  const canGenerate = !isGenerating && !disabled && status !== CompanySnapshotStatus.COMPLETED

  return (
    <GridRow
      container
      spacing={16}
      style={{ padding: '8px 0', borderRadius: 4 }}
      alignItems="center"
    >
      <Grid item xs={6} sm={4} style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          Company Products
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4} style={{ display: 'flex', alignItems: 'center' }}>
        <Grid container alignItems="center" spacing={8}>
          <Grid item>{getStatusIcon(status)}</Grid>
          <Grid item>
            <StatusText status={status} onClick={onStatusClick}>
              {status}
            </StatusText>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6} sm={4} style={{ display: 'flex', alignItems: 'center' }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item>
            <Tooltip
              title={
                isGenerating
                  ? 'Generating company products snapshot...'
                  : canGenerate
                  ? 'Generate a new company products snapshot'
                  : 'Select a company to generate snapshot'
              }
            >
              <span>
                <StyledGenerateButton
                  size="small"
                  onClick={onGenerateSnapshot}
                  disabled={!canGenerate}
                  isGenerating={isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Generate'}
                </StyledGenerateButton>
              </span>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip
              title={
                canDownload
                  ? 'Download the latest snapshot'
                  : 'No valid snapshot available for download'
              }
            >
              <span>
                <StyledDownloadButton
                  onClick={() => snapshot && onDownloadSnapshot?.(snapshot)}
                  disabled={!canDownload}
                  canDownload={!!canDownload}
                >
                  <CloudDownloadIcon />
                </StyledDownloadButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </GridRow>
  )
}

export const CompanySnapshotGroup: React.FC<CompanySnapshotGroupProps> = ({
  isLoading = false,
  isCompanySelected = false,
  hasAvailableShops = false,
  latestSnapshot,
  onGenerateSnapshot,
  onDownloadSnapshot,
  isGenerating = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const validSnapshot = hasValidSnapshot(latestSnapshot)

  const handleStatusClick = () => {
    if (latestSnapshot) {
      setModalOpen(true)
    }
  }

  return (
    <Grid container direction="column" spacing={8}>
      <Grid item>
        <SnapshotHeaderRow />
      </Grid>
      <Grid item style={{ height: '70px', paddingBottom: 0 }}>
        <SnapshotRow
          snapshot={latestSnapshot}
          onGenerateSnapshot={onGenerateSnapshot}
          onDownloadSnapshot={onDownloadSnapshot}
          onStatusClick={handleStatusClick}
          isGenerating={isGenerating}
          disabled={isLoading || !isCompanySelected || !hasAvailableShops}
        />
      </Grid>
      {isCompanySelected && !hasAvailableShops && (
        <Grid item>
          <Chip
            label="⚠️ No shops available for this company"
            color="secondary"
            style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              fontWeight: 500,
              marginTop: 8,
            }}
          />
        </Grid>
      )}
      {isCompanySelected && hasAvailableShops && !validSnapshot && !isGenerating && (
        <Grid item>
          <Chip
            label="⚠️ A valid product snapshot is required to enable migration options"
            color="secondary"
            style={{
              backgroundColor: '#fff3e0',
              color: '#e65100',
              fontWeight: 500,
              marginTop: 8,
            }}
          />
        </Grid>
      )}
      <SnapshotInfoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        snapshot={latestSnapshot}
      />
    </Grid>
  )
}



// WEBPACK FOOTER //
// ./src/containers/Settings/Development/MigrateCompanyProducts/CompanySnapshotGroup.tsx